<script setup lang="ts">
const localePath = useLocalePath()
const { locale } = useI18n()

const { globalData } = useVildmarkenState()
const data = ref(globalData.value?.footer)

const fontVariation = ref(0)

const updateFontVariation = () => {
  const section = document.querySelector('.footer') as HTMLElement
  const scrollPercentage = (window.scrollY - section.offsetTop) / section.offsetHeight
  // Clamp the value between 0 and -70
  fontVariation.value = Math.trunc(Math.min(0, Math.max(-70, -70 * scrollPercentage)))
}

onMounted(() => {
  window.addEventListener('scroll', updateFontVariation)
})

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateFontVariation)
})

watch(locale, async () => {
  console.log('locale changed, reload footer data', locale.value)
  const { data: newData } = await useSanityQuery(getFooter, { language: locale.value })
  data.value = newData.value
})

const { isMobileOrTablet } = useDevice()
const { width: innerWidth } = useWindowSize()

const isMobile = computed(() => {
  if (import.meta.server) return isMobileOrTablet
  return innerWidth.value < 900
})
</script>

<template>
  <footer
    class="theme-green-dark footer px-bleed relative flex min-h-[100svh] flex-col justify-between gap-20 bg-(--theme-bg) bg-linear-0 from-(--green-2) to-(--green-1) pt-36 pb-(--grid-bleed) text-(--theme-fg) lg:h-screen lg:gap-0 lg:pt-32"
  >
    <ElementsThemeSetter theme="green-light-green" :is-footer="true" />

    <div
      class="wrapper relative z-[2] flex w-full flex-col items-start gap-20 self-center lg:flex-row lg:gap-(--grid-gap)"
    >
      <h2
        v-if="data?.title"
        class="font-display title lg:w-cols-6 text-2xl lg:text-3xl"
        :style="{ '--font-weight': fontVariation }"
      >
        <CommonExposedText
          v-if="isMobile"
          :text="data?.title"
          :exposure="[-40, -80]"
          :animated="true"
        />
        <CommonProximityText
          v-else
          :text="data?.title"
          :radius="300"
          falloff="easeInOut"
          :exposure="{ from: -20, to: -70 }"
        />
      </h2>
      <div class="columns flex flex-row gap-(--grid-gap)">
        <div v-for="(column, index) in data?.columns" :key="index" class="w-cols-2 lg:w-cols-3">
          <h3 class="mb-10 text-sm font-medium text-(--green-3) uppercase">{{ column.title }}</h3>

          <ul class="inline-grid gap-4">
            <li v-for="(item, idx) in column.menuItems" :key="idx" class="text-base">
              <NuxtLink
                :href="item.linkType === 'url' ? item.url : localePath(item.page.url)"
                :target="item.target"
              >
                {{ item.label }}
              </NuxtLink>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="bottom-wrapper flex flex-col gap-20 lg:flex-row lg:items-end lg:gap-(--grid-gap)">
      <div class="logo grid w-full gap-4">
        <div v-if="data?.managedBy?.text" class="managedBy text-xs font-medium uppercase">
          {{ data?.managedBy?.text }}
        </div>
        <nuxt-link
          :to="data?.managedBy?.link"
          class="w-cols-2 relative block"
          aria-label="Hempel Logo"
        >
          <ElementsHempelLogo class="h-full w-full" />
        </nuxt-link>
      </div>
      <div class="more flex w-full flex-col justify-between gap-5 font-medium lg:flex-row">
        <ul class="inline-flex gap-10 text-xs uppercase">
          <li v-for="(item, index) in data?.additionalLinks" :key="index" class="underline">
            <NuxtLink
              :href="item.linkType === 'url' ? item.url : localePath(item.page.url)"
              :target="item.target"
            >
              {{ item.label }}
            </NuxtLink>
          </li>
          <li>
            <a href="javascript:CookieConsent.renew();" class="">{{
              $t('cookies.consent.link.text')
            }}</a>
          </li>
          <li class="">
            <div v-if="data?.cvr" class="cvr text-xs uppercase">{{ data?.cvr }}</div>
          </li>
          <li>
            <LanguageSelector />
          </li>
        </ul>
        <div v-if="data?.copyright" class="copyright text-xs uppercase">
          {{ data?.copyright + ' ' + new Date().getFullYear() }}
        </div>
      </div>
    </div>
  </footer>
</template>
<style lang="postcss">
.footer .title {
  font-variation-settings: 'EXPO' var(--font-weight);
}
</style>
